import { first } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmptyResponse, IMobizHttpService, IMobizStateService, SerializableObjectList } from '../../common-react/core';
import { Inject, Injectable, InjectionToken } from '../../common-react/injection';

import { Address } from '../data/address.do';
import { FamilyMembersNewAddress } from '../data/family-members-new-address.do';
import { Family } from '../data/family.do';
import { PosturinnApiEndpointConfiguration } from '../other/api-endpoint-configuration.interface';
import { PosturinnServerApi } from '../other/posturinn-server-api.enum';

export interface IFamilyMembersService {
  getFamilyMembers(): Observable<Family>;
  updateFamilyMembersAddress(
    familyMembersAndAddress: FamilyMembersNewAddress
  ): Observable<EmptyResponse>;
  getPostalCodesForAddress(address: string): Observable<Address[]>;
}

export const IFamilyMembersService: InjectionToken<IFamilyMembersService>
  = new InjectionToken<IFamilyMembersService>('IFamilyMembersService');

@Injectable()
export class FamilyMembersService implements IFamilyMembersService {

  private readonly accountId: string;

  constructor(
    @Inject(IMobizHttpService)
    private httpService: IMobizHttpService,
    @Inject(IMobizStateService)
    private stateService: IMobizStateService,
  ) {
    if (this.stateService.currentUser.value === null) {
      // NOTE: TP-8573 Display an "Access Denied" error page.
      throw new Error('Access Denied. Please log in.');
    }
    const membership = first(this.stateService.currentUser.value.membership);
    if (!membership) {
      throw new Error('Invalid membership data.');
    }
    // Posturinn accounts have their own membership organization.
    this.accountId = membership.organizationId;
  }

  public getFamilyMembers(): Observable<Family> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.AccountFamilyMembers,
      urlParameters: {
        accountId: this.accountId,
      },
    };
    return this.httpService.get(new Family(), config);
  }

  public updateFamilyMembersAddress(
    familyMembersAndAddress: FamilyMembersNewAddress
  ): Observable<EmptyResponse> {
    /**
     * TODO: TP-9533 We're sending a JSON because the provided FamilyMembersNewAddress instance is invalid for the backend.
     *
     * @see mobiz-backend:UserProfileController.ChangeAddressOfFamilyMembers()
     */
    const requestPayload = JSON.stringify({
      familyMembersToChangeAddressFor: familyMembersAndAddress.familyMembersToChangeAddressFor,
      streetName: familyMembersAndAddress.addressChange.streetName,
      streetNumber: familyMembersAndAddress.addressChange.streetNumber,
      apartmentNumber: familyMembersAndAddress.addressChange.apartmentNumber,
      postalCode: familyMembersAndAddress.addressChange.postalCode,
      city: familyMembersAndAddress.addressChange.city,
      additionalInformation: familyMembersAndAddress.addressChange.additionalInformation
    });

    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.AccountFamilyMembers,
      urlParameters: {
        accountId: this.accountId,
      },
      body: requestPayload,
    };
    return this.httpService.put(new FamilyMembersNewAddress(), config);
  }

  public getPostalCodesForAddress(address: string): Observable<Address[]> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.AddressLookup,
      queryParameters: {
        address: address
      }
    };
    const list: SerializableObjectList<Address> = new SerializableObjectList(Address);
    return this.httpService.get(list, config).pipe(
      map((response: SerializableObjectList<Address>): Address[] => {
        return response.items ? response.items : [];
      })
    );
  }
}
